<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { Listing, Review } from '../interfaces/listing'
import ReviewItem from './ReviewItem.vue'
import RatingReviews from './RatingReviews.vue'
import { updateSortBy } from '@/Composables/use-listing-search'
import AppSorting from '@/Components/sorting/AppSorting.vue'
import AppPagination from '@/Components/pagination/AppPagination.vue'
import {
    changeSelectedMockHostelSorting,
    mockHostelSorting,
    sortCriteria,
    updateSortCriteria
} from '@/Composables/appSortingLogic'
import {
    setCurrentPaginationPage,
    currentPaginationPage,
    setMaxAmountPaginationPages,
    maxAmountPaginationPages
} from '@/Composables/appPaginationLogic'
import { ResponseData, SelectedOption } from '@/Interfaces/AppSorting'
import { User } from '@/types'
import { Link } from '@inertiajs/vue3'
import TheIcon from '@/Components/TheIcon.vue'
import { HiddenGemzType } from '@/Interfaces/Common'
import HiddenGemz from '@/Components/hidden-gemz/HiddenGemz.vue'
import { AdData } from '@/Pages/the-city/interfaces/City'
import TheCityAds from '@/Pages/the-city/components/AdFeatures/TheCityAds.vue'

type Props = {
    reviews: Review[],
    listing: Listing,
    pagesNumber: number,
    user: User | null,
    hiddenGemz?: HiddenGemzType[],
    adForListing?: AdData
}

const props = defineProps<Props>()

const emit = defineEmits([
    'getSortedReviews',
    'updateTabReviews',
    'scrollToTab',
    'tabsSwitchAndScroll'
])

onMounted(() => {
    const request = {
        state: {
            url: '/listing-reviews/',
            listing: { id: props.listing.id },
            sortedBy: 'commentDate',
            searchString: '',
            currentPage: currentPaginationPage.value
        }
    }

    setMaxAmountPaginationPages(props.pagesNumber)

    updateSortCriteria('commentDate')

    if (props.listing.id && currentPaginationPage.value && !props.reviews.length) {
        emit('getSortedReviews', request, (data: ResponseData) => emit('updateTabReviews', data), (data: ResponseData) => setMaxAmountPaginationPages(data.pagesNumber))
    }
})

const searchQuery = ref('')

const searchReviews = (event: Event): void => {
    const target = event.target as HTMLInputElement

    searchQuery.value = target.value

    setCurrentPaginationPage(1)

    const request = {
        state: {
            url: '/listing-reviews/',
            listing: { id: props.listing.id },
            sortedBy: sortCriteria.value,
            searchString: searchQuery.value,
            currentPage: currentPaginationPage.value
        }
    }

    emit('getSortedReviews', request, (data: ResponseData) => emit('updateTabReviews', data), (data: ResponseData) => setMaxAmountPaginationPages(data.pagesNumber))
}

const sortingIsOpen = ref(false)

const changeSortingIsOpen = (status: boolean): void => {
    sortingIsOpen.value = status
}

function sortList (item: SelectedOption): void {
    if (item.sortBy === mockHostelSorting.value.selected.sortBy) {
        return
    }

    setCurrentPaginationPage(1)

    changeSelectedMockHostelSorting(item)

    changeSortingIsOpen(false)

    updateSortCriteria(item.sortBy)

    if (!props.reviews.length) {
        return
    }

    const request = {
        state: {
            url: '/listing-reviews/',
            listing: { id: props.listing.id },
            sortedBy: item.sortBy,
            searchString: searchQuery.value,
            currentPage: currentPaginationPage.value
        }
    }

    emit('getSortedReviews', request, (data: ResponseData) => emit('updateTabReviews', data), (data: ResponseData) => setMaxAmountPaginationPages(data.pagesNumber))

}

const changePage = (page: number): void => {
    if (currentPaginationPage.value === page) {
        return
    }

    if (page >= 1 && page <= maxAmountPaginationPages.value) {
        setCurrentPaginationPage(page)

        emit('scrollToTab')

        const request = {
            state: {
                url: '/listing-reviews/',
                listing: { id: props.listing.id },
                sortedBy: sortCriteria.value,
                searchString: searchQuery.value,
                currentPage: currentPaginationPage.value
            }
        }

        emit('getSortedReviews', request, (data: ResponseData) => emit('updateTabReviews', data), (data: ResponseData) => setMaxAmountPaginationPages(data.pagesNumber))
    }
}

</script>

<template>
    <div class="reviews-tab">
        <div v-if="hiddenGemz && hiddenGemz.length">
            <HiddenGemz
                v-for="(hg, key) in hiddenGemz"
                :key
                :="hg"
                :user="user"
                :is-single-listing="true"
                class="p-3 mb-lg-5 mb-3"
                @tabs-switch-and-scroll="emit('tabsSwitchAndScroll', $event)"
            />
        </div>
        
        <div v-if="adForListing" class="d-inline-block">
            <TheCityAds
                :url="adForListing.url"
                :image="adForListing.image"
                :content="adForListing.content"
                class="p-3 mb-lg-5 mb-3"
                @tabs-switch-and-scroll="emit('tabsSwitchAndScroll', $event)"
            />
        </div>
        <div class="d-flex reviews-tab-wrap justify-content-between flex-lg-row flex-column">
            <div class="reviews-tab-rating">
                <RatingReviews :listing="listing" />
                <div class="mt-3">
                    <p class="text-sm">
                        Stayed at this hostel? Share your experience and help fellow travelers in the Hostelz Community
                        by leaving a detailed review.
                    </p>

                    <Link
                        class="btn btn-lg reviews-tab-btn mt-4 text-decoration-none"
                        :href="route('submitRating', listing.id)"
                    >
                        <TheIcon
                            icon-id="close-icon-2"
                            width="22"
                            height="22"
                            stroke="#4A5268"
                        />
                        Add Your Review
                    </Link>

                    <p class="text-sm mt-4">
                        We take authenticity seriously. Each review must be verified with a valid email and reservation
                        confirmation number. This helps us keep the reviews trustworthy and useful for everyone.
                    </p>

                    <p v-if="!user" class="text-sm">
                        <Link :href="route('signupShow')">
                            Create your free account
                        </Link>
                        to easily track your hostel stays and reviews.
                    </p>
                </div>
            </div>
            <div class="reviews-tab-reviews-wrap w-100">
                <div class="d-flex flex-sm-row flex-column align-items-sm-center justify-content-between mb-5 mb-lg-2">
                    <AppSorting
                        :options="mockHostelSorting.options"
                        :selected="mockHostelSorting.selected"
                        class="order-1 order-sm-0"
                        :sorting-is-open="sortingIsOpen"
                        @update-sort-by="updateSortBy"
                        @sort-list="sortList"
                        @change-sorting-is-open="changeSortingIsOpen"
                    />
                    <div class="reviews-tab-input mb-3 mb-sm-0">
                        <input
                            name="features"
                            placeholder="Search for keywords"
                            type="text"
                            class="form-control rounded-xl"
                            :value="searchQuery"
                            autocomplete="off"
                            autocorrect="off"
                            autocapitalize="off"
                            @input="searchReviews"
                        >
                    </div>
                </div>

                <ReviewItem
                    v-if="reviews.length"
                    :reviews="reviews"
                    :search-string="searchQuery"
                />


                <p v-else class="text-uppercase font-weight-bold text-center">
                    No Reviews yet. Be the first!
                </p>

                <AppPagination
                    v-if="maxAmountPaginationPages !== 1 && reviews.length"
                    :total-pages="maxAmountPaginationPages"
                    :current-page="currentPaginationPage"
                    @page-changed="changePage"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.reviews-tab {
    &:deep(.hidden-gemz) {
        .hg-content h3 {
            font-size: 18px;
            margin-bottom: 16px;

            @media (min-width: 576px) {
                font-size: 20px;
            }

            @media (min-width: 992px) {
                font-size: 24px;
            }

            @media (min-width: 1200px) {
                font-size: 32px;
            }
        }
    }

    &-input {

        input {
            background-image: url("data:image/svg+xml, %3Csvg width='22' height='22' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M3.75 10.0833C3.75 6.58553 6.58553 3.75 10.0833 3.75C13.5811 3.75 16.4167 6.58553 16.4167 10.0833C16.4167 11.7875 15.7436 13.3345 14.6488 14.4729C14.6164 14.4981 14.5852 14.5256 14.5554 14.5554C14.5256 14.5852 14.4981 14.6164 14.4728 14.6488C13.3344 15.7436 11.7875 16.4167 10.0833 16.4167C6.58553 16.4167 3.75 13.5811 3.75 10.0833ZM15.2264 16.6407C13.81 17.7532 12.0241 18.4167 10.0833 18.4167C5.48096 18.4167 1.75 14.6857 1.75 10.0833C1.75 5.48096 5.48096 1.75 10.0833 1.75C14.6857 1.75 18.4167 5.48096 18.4167 10.0833C18.4167 12.0242 17.7532 13.81 16.6407 15.2265L19.9571 18.5429C20.3476 18.9334 20.3476 19.5666 19.9571 19.9571C19.5666 20.3477 18.9334 20.3477 18.5429 19.9571L15.2264 16.6407Z' fill='%23787D8D'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: 5% 50%;
            padding-left: 54px;
            background-color: transparent;
            border: 2px solid var(--accent-bg);
            padding-top: 9px;
            padding-bottom: 9px;

            @media (min-width: 576px) {
                padding-right: 54px;
                background-position: 7% 50%;
            }

            @media (min-width: 768px) {
                padding-right: 24px;
            }

            @media (min-width: 992px) {
                padding-right: 24px;
                background-position: 8% 50%;
            }
        }
    }

    &-btn {
        color: var(--main-text);
        background-color: var(--accent-bg);
        border-color: var(--accent-bg);
        box-shadow: none;
        transition: background-color 0.3s, border-color 0.3s;
        display: flex;
        justify-content: center;
        gap: 8px;

        width: 100%;

        &:hover {
            background-color: #cbd1db;
            border-color: #cbd1db;
        }

        &:deep(.the-icon-component) {
            display: block;

            svg {
                transform: rotate(45deg);
            }
        }
    }

    &-reviews {
        &-wrap {
            &:deep(.reviews-item) {

                .review-item-reviews-wrap {
                    gap: 32px;
                    flex-direction: column;

                    @media (min-width: 768px) {
                        flex-direction: row;
                        gap: 16px;
                    }
                }

                .review-item-avatar-wrap {
                    flex-basis: calc(100% - 70% - 16px);
                }

                .review-item-inner {
                    flex-basis: calc(100% - 30%);
                }
            }

            &:deep(.hostel-sorting) {
                .dropdown-menu {
                    right: auto;

                    @media (min-width: 576px) {
                        left: 0;
                        right: 0;
                    }
                }
            }
        }
    }


    &-wrap {
        gap: 32px;

        @media (min-width: 992px) {
            gap: 24px;
        }

        @media (min-width: 1200px) {
            gap: 64px;
        }
    }

    &-rating {
        flex-shrink: 0;
        width: 100%;

        @media (min-width: 992px) {
            max-width: 300px;
        }

        @media (min-width: 1200px) {
            max-width: 400px;
        }
    }
}
</style>